import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './EvalsSkeleton.css';

/** Question rows drawn under the table's header. */
const ROWS = [0, 1, 2, 3];
/** The Segmented's two options: Questions and Executions. */
const SEGMENTS = [0, 1];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`evals-skeleton__bone ${className}`} />
);

export interface EvalsSkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of Evaluation Tests (SL-1948). One shape from a cold load to
 * data: `RouteSkeleton` (inside the app chrome), the agent layouts while the
 * agent loads, the route's Suspense fallback and the page while its questions
 * load all draw this.
 *
 * The page as it arrives: breadcrumb, then the card — title and subtitle with
 * Execute beside them, the Segmented, the questions' count with Add beside it,
 * and the table.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it. No
 * `aria-busy`: on a live region it tells assistive tech to hold the
 * announcement.
 */
export const EvalsSkeleton: React.FC<EvalsSkeletonProps> = ({ standalone = false }) => {
  const { t } = useTranslation(NS);
  const text = t('loading');

  return (
    <div
      className={`evals-skeleton${standalone ? ' evals-skeleton--standalone' : ''}`}
      role="status"
      data-testid="evals-skeleton"
    >
      <span className="evals-skeleton__sr-only">{text}</span>
      <div className="evals-skeleton__header" aria-hidden="true">
        <Bone className="evals-skeleton__crumb" />
      </div>
      <div className="evals-skeleton__card" aria-hidden="true">
        <div className="evals-skeleton__head">
          <div className="evals-skeleton__heading">
            <Bone className="evals-skeleton__title" />
            <Bone className="evals-skeleton__line evals-skeleton__line--subtitle" />
          </div>
          <Bone className="evals-skeleton__button" />
        </div>
        <div className="evals-skeleton__segmented">
          {SEGMENTS.map((segment) => (
            <Bone key={segment} className="evals-skeleton__segment" />
          ))}
        </div>
        <div className="evals-skeleton__panel-head">
          <Bone className="evals-skeleton__line evals-skeleton__line--count" />
          <Bone className="evals-skeleton__button" />
        </div>
        <div className="evals-skeleton__table">
          <div className="evals-skeleton__row evals-skeleton__row--head">
            <Bone className="evals-skeleton__cell" />
            <Bone className="evals-skeleton__cell" />
            <Bone className="evals-skeleton__cell" />
          </div>
          {ROWS.map((row) => (
            <div key={row} className="evals-skeleton__row">
              <Bone className="evals-skeleton__tag" />
              <Bone className="evals-skeleton__cell" />
              <Bone className="evals-skeleton__cell" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
